import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Pagination,
  Alert,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { DataTable, FilterBar, SchoolPicker, EmptyState } from './primitives';
import RoleChip from '../RoleChip';
import { ROLES, isMasterAdmin, isSchoolScopedRole } from '../../constants/roles';
import { useQueryState, useScrollRestore } from '../../hooks/useQueryState';
import StaffDrawer from './StaffDrawer';

const PAGE_SIZE = 25;

// Every list-view filter this page understands, doubling as
// useQueryState()'s schema — see StudentsPage.js's own copy of this comment
// (and hooks/useQueryState.js) for how a key's default value decides its URL
// coercion.
const QUERY_DEFAULTS = {
  q: '',
  school: '',
  role: 'all',
  access: 'all',
  sort: 'name',
  order: 'asc',
  page: 1,
};

const ACCESS_FILTER_OPTIONS = [
  { value: 'all', label: 'All access' },
  { value: 'granted', label: 'Granted' },
  { value: 'revoked', label: 'Revoked' },
];

// Which non-STUDENT roles a viewer's "All roles" filter (and the unfiltered
// page load) actually fetches — the `roles=` comma-list GET /api/admin/users
// gained for exactly this page (see app/routes/admin.py's docstring). A
// SCHOOL_ADMIN never sees MASTER_ADMIN/legacy "admin" rows here, matching
// the scoping matrix in this file's header comment; a TEACHER never reaches
// this page at all (see AdminDashboardV2.js's route guard), so there's no
// third branch to handle.
function staffRolesForViewer(currentUserRole) {
  if (isMasterAdmin(currentUserRole)) {
    return [ROLES.TEACHER, ROLES.SCHOOL_ADMIN, ROLES.MASTER_ADMIN, ROLES.LEGACY_ADMIN];
  }
  return [ROLES.TEACHER, ROLES.SCHOOL_ADMIN];
}

// A row's "Classes" column: a TEACHER's *taught* classes (populated by
// GET /api/admin/users?include=groups sourcing a TEACHER row from
// GroupTeacher — see that endpoint's docstring), rendered with the same
// compact "N classes · Algebra 1, Biology 9" treatment StudentsPage.js's
// ClassesSummaryCell uses. A SCHOOL_ADMIN/MASTER_ADMIN row never teaches a
// class, so it always reads "—" — not "No classes" (StudentsPage's wording
// for a student who *could* have classes but doesn't), since the concept
// doesn't apply to that row at all.
function ClassesCell({ role, classes }) {
  if (role !== ROLES.TEACHER) {
    return (
      <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
        —
      </Typography>
    );
  }
  if (!classes || classes.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
        No classes
      </Typography>
    );
  }
  const names = classes.map((c) => c.name).join(', ');
  return (
    <Tooltip title={names}>
      <Box sx={{ maxWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
          {classes.length} class{classes.length === 1 ? '' : 'es'}
        </Typography>
        <Typography variant="caption" noWrap sx={{ display: 'block', color: adminColors.inkSoft }}>
          {names}
        </Typography>
      </Box>
    </Tooltip>
  );
}

// Read-only Granted/Revoked chip — same visual language (and colors) as
// studentStatus.js's STATUS_META.active/no_access, but there's no
// "invited"/"active" distinction here (that's derived from tutoring-message
// activity, meaningless for staff — see this file's header comment), so
// this is a plain boolean chip rather than a three-way derived status.
function AccessCell({ granted }) {
  return (
    <Chip
      label={granted ? 'Granted' : 'Revoked'}
      size="small"
      sx={
        granted
          ? { bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, height: 22, fontSize: '0.72rem' }
          : { bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 700, height: 22, fontSize: '0.72rem' }
      }
    />
  );
}

// Staff list — teachers, school admins, and (master-admin view only) other
// master admins / the legacy "admin" role. Sibling of StudentsPage.js, split
// out of the old combined "All Users" page as part of the admin-console v2
// staff split:
//
//   Teacher        — no access at all (no nav item; this route redirects,
//                    see AdminDashboardV2.js's `staff/:username?` route).
//   School admin   — teachers + school admins in their own school only.
//   Master admin   — everyone: teachers, school admins, other master
//                    admins (+ legacy lowercase "admin"), every school.
//
// Deliberately no Status column/filter (unlike StudentsPage) — status there
// is derived from tutoring-message activity, which no staff role ever
// generates, so it would only ever read "Invited" regardless of how active
// an admin or teacher actually is. "Access" (a real access_granted column)
// is the one relevant on/off signal for a staff row and gets its own filter
// instead.
//
// `groups` (same GET /api/groups array AdminDashboardV2 already fetches) is
// accepted for prop-shape parity with StudentsPage but unused here — a
// teacher's taught classes come from this page's own `include=groups` fetch
// (server-scoped already), and there's no class-membership bulk action on
// this page to populate a class picker for.
export default function StaffPage({
  currentUserRole,
  currentUser,
  notify,
  confirmAction,
  onRoleChange,
  onSchoolChange,
  onDeleteUser,
  onAccessToggle,
  onResetPassword,
  onOpenAddUserDialog,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams();
  const [qs, setQueryState] = useQueryState(QUERY_DEFAULTS);

  const isMasterAdminRole = isMasterAdmin(currentUserRole);
  // A SCHOOL_ADMIN is locked to their own school server-side — same check
  // StudentsPage uses to decide whether a School *filter* is even a
  // meaningful control to show. (A TEACHER would also be "locked" by this
  // check, but never reaches this page at all.)
  const isSchoolLocked = isSchoolScopedRole(currentUserRole);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const accessActive = qs.access !== QUERY_DEFAULTS.access;

  const fetchList = useCallback(
    async (signal) => {
      setLoadingList(true);
      setListError(null);
      const params = new URLSearchParams();
      if (qs.q) params.set('search', qs.q);
      if (isMasterAdminRole && qs.school) params.set('school', qs.school);
      if (qs.role === QUERY_DEFAULTS.role) {
        params.set('roles', staffRolesForViewer(currentUserRole).join(','));
      } else if (qs.role === ROLES.MASTER_ADMIN) {
        // The Staff page's "Master Admin" filter groups MASTER_ADMIN and
        // the legacy lowercase "admin" role together (privilege-equivalent
        // everywhere — see isMasterAdmin() in constants/roles.js).
        params.set('roles', [ROLES.MASTER_ADMIN, ROLES.LEGACY_ADMIN].join(','));
      } else {
        params.set('role', qs.role);
      }
      params.set('include', 'groups');
      params.set('sort', qs.sort);
      params.set('order', qs.order);
      if (accessActive) {
        // No `access_granted`-only filter exists server-side (`status`
        // conflates it with tutoring-message activity, meaningless here —
        // see this file's header comment) — fetch the backend's max page
        // size once and filter+paginate client-side instead, same
        // workaround StudentsPage's own status filter uses.
        params.set('page', '1');
        params.set('page_size', '100');
      } else {
        params.set('page', String(qs.page));
        params.set('page_size', String(PAGE_SIZE));
      }
      try {
        const res = await authFetch(
          `${config.API_BASE_URL}/api/admin/users?${params.toString()}`,
          {},
          { signal }
        );
        const data = await handleApiResponse(res, navigate);
        if (data === null || signal?.aborted) return; // 401 — already redirected
        let newTotal;
        if (accessActive) {
          const wantGranted = qs.access === 'granted';
          const filtered = (data.users || []).filter((u) => (u.access_granted !== false) === wantGranted);
          newTotal = filtered.length;
          const start = (qs.page - 1) * PAGE_SIZE;
          setRows(filtered.slice(start, start + PAGE_SIZE));
          setTotal(newTotal);
        } else {
          newTotal = data.total || 0;
          setRows(data.users || []);
          setTotal(newTotal);
        }
        const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
        if (qs.page > newTotalPages) {
          setQueryState({ page: newTotalPages });
        }
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted) return;
        setListError(`Couldn't load staff. ${describeApiError(err)}`);
      } finally {
        if (!signal?.aborted) setLoadingList(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accessActive is derived from qs.access, already listed
    [qs.q, qs.school, qs.role, qs.access, qs.sort, qs.order, qs.page, isMasterAdminRole, currentUserRole, navigate, setQueryState]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList]);

  // Same window-as-scroll-container adapter StudentsPage.js uses — see its
  // own comment on windowScrollRefBox for why (AdminDashboardV2's content
  // area scrolls at the window level, no inner `overflow: auto` container).
  const windowScrollRefBox = useRef(null);
  if (!windowScrollRefBox.current) {
    windowScrollRefBox.current = {
      get scrollTop() {
        return window.scrollY || window.pageYOffset || 0;
      },
      set scrollTop(v) {
        window.scrollTo(0, v);
      },
      addEventListener: (type, handler, opts) => window.addEventListener(type, handler, opts),
      removeEventListener: (type, handler) => window.removeEventListener(type, handler),
    };
  }
  useScrollRestore(windowScrollRefBox, `staff-list:${location.search}`);

  const handleRowClick = (row) => {
    navigate({ pathname: `/admin/staff/${encodeURIComponent(row.username)}`, search: location.search });
  };

  const closeDrawer = () => {
    navigate({ pathname: '/admin/staff', search: location.search });
  };

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        render: (row) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
              {row.username || 'Unknown'}
            </Typography>
            {row.email && (
              <Typography variant="caption" noWrap sx={{ display: 'block', color: adminColors.inkSoft, maxWidth: 220 }}>
                {row.email}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        render: (row) => <RoleChip role={row.role} />,
      },
      {
        key: 'school',
        label: 'School',
        sortable: true,
        render: (row) => (
          <Typography variant="body2" sx={{ color: row.school ? adminColors.ink : adminColors.inkSoft }}>
            {row.school || 'Unassigned'}
          </Typography>
        ),
      },
      {
        key: 'classes',
        label: 'Classes',
        render: (row) => <ClassesCell role={row.role} classes={row.classes} />,
      },
      {
        // Server-side sort key is literally `status` (GET /api/admin/users
        // orders by the `access_granted` column for `sort=status` — see
        // that endpoint's docstring) even though this column reads
        // "Access", not "Status" — reusing it exactly as-is rather than
        // inventing a same-shaped `access` sort value the backend has no
        // matching case for.
        key: 'status',
        label: 'Access',
        sortable: true,
        render: (row) => <AccessCell granted={row.access_granted !== false} />,
      },
    ],
    []
  );

  const roleFilterOptions = useMemo(() => {
    const opts = [
      { value: QUERY_DEFAULTS.role, label: 'All roles' },
      { value: ROLES.TEACHER, label: 'Teacher' },
      { value: ROLES.SCHOOL_ADMIN, label: 'School Admin' },
    ];
    if (isMasterAdminRole) {
      opts.push({ value: ROLES.MASTER_ADMIN, label: 'Master Admin' });
    }
    return opts;
  }, [isMasterAdminRole]);

  const hasActiveFilters = ['q', 'school', 'role', 'access'].some(
    (key) => (qs[key] ?? '') !== (QUERY_DEFAULTS[key] ?? '')
  );

  const emptyStateNode = hasActiveFilters ? (
    <EmptyState
      icon={<BadgeIcon />}
      title="No matches"
      description="Try a different search, or clear filters to see everyone."
      action={
        <Button variant="outlined" onClick={() => setQueryState({ ...QUERY_DEFAULTS })} sx={TOUCH_HIT_SX}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<BadgeIcon />}
      title="No staff yet"
      description='Use "Add New User" to create or invite a teacher or admin.'
      action={
        <Button variant="contained" onClick={onOpenAddUserDialog} sx={TOUCH_HIT_SX}>
          Add New User
        </Button>
      }
    />
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (qs.page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(qs.page * PAGE_SIZE, total);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: adminColors.ink }}>
            Staff
          </Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.5 }}>
            {isMasterAdminRole
              ? 'Every teacher and admin, across every school you can see.'
              : 'Teachers and admins at your school.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onOpenAddUserDialog}
          sx={{
            backgroundColor: adminColors.accent,
            '&:hover': { backgroundColor: adminColors.accentDark },
            borderRadius: radius.control,
            px: 3,
            py: 1,
            ...TOUCH_HIT_SX,
          }}
        >
          Add New User
        </Button>
      </Box>

      {listError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchList()}>
              Retry
            </Button>
          }
        >
          {listError}
        </Alert>
      )}

      <FilterBar
        queryState={qs}
        setQueryState={setQueryState}
        defaults={QUERY_DEFAULTS}
        searchKey="q"
        searchPlaceholder="Search by name or school…"
        resetOnSearch={['page']}
        sx={{ mb: 2.5 }}
      >
        {!isSchoolLocked && (
          <SchoolPicker
            value={qs.school}
            onChange={(v) => setQueryState({ school: v, page: QUERY_DEFAULTS.page })}
            currentUserRole={currentUserRole}
            emptyLabel="All schools"
            label="School"
            size="small"
            fullWidth={false}
            sx={{ minWidth: 170 }}
          />
        )}

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="staff-role-filter-label">Role</InputLabel>
          <Select
            labelId="staff-role-filter-label"
            label="Role"
            value={qs.role}
            onChange={(e) => setQueryState({ role: e.target.value, page: QUERY_DEFAULTS.page })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            {roleFilterOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="staff-access-filter-label">Access</InputLabel>
          <Select
            labelId="staff-access-filter-label"
            label="Access"
            value={qs.access}
            onChange={(e) => setQueryState({ access: e.target.value, page: QUERY_DEFAULTS.page })}
            sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
          >
            {ACCESS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(24,24,27,0.1)', display: { xs: 'none', sm: 'block' } }} />

        <Button
          size="small"
          onClick={() =>
            setQueryState({
              sort: 'name',
              order: qs.sort === 'name' && qs.order === 'asc' ? 'desc' : 'asc',
              page: QUERY_DEFAULTS.page,
            })
          }
          startIcon={
            qs.sort === 'name' && qs.order === 'desc' ? (
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            ) : (
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            ...TOUCH_HIT_SX,
            fontWeight: 600,
            color: qs.sort === 'name' ? adminColors.accent : adminColors.inkSoft,
            '&:hover': { backgroundColor: adminColors.surface },
          }}
        >
          Name {qs.sort === 'name' && qs.order === 'desc' ? 'Z–A' : 'A–Z'}
        </Button>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        sortKey={qs.sort}
        sortDirection={qs.order}
        onSortChange={(key, dir) => setQueryState({ sort: key, order: dir, page: QUERY_DEFAULTS.page })}
        onRowClick={handleRowClick}
        getRowAriaLabel={(row) => `Open ${row.username}`}
        loading={loadingList}
        emptyState={emptyStateNode}
      />

      {total > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            mt: 3,
            opacity: loadingList ? 0.5 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={qs.page}
              onChange={(_, v) => setQueryState({ page: v })}
              color="primary"
              shape="rounded"
            />
          )}
          <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
            {rangeStart}–{rangeEnd} of {total}
          </Typography>
        </Box>
      )}

      <StaffDrawer
        username={username}
        onClose={closeDrawer}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        notify={notify}
        confirmAction={confirmAction}
        onRoleChange={onRoleChange}
        onSchoolChange={onSchoolChange}
        onAccessToggle={onAccessToggle}
        onDeleteUser={onDeleteUser}
        onResetPassword={onResetPassword}
        onMutated={fetchList}
      />
    </Box>
  );
}
